// @file Padlet half ellipse shape for Tldraw
// To render the half ellipse in Tldraw. This is for Jamboard half ellipse shapes when converting to Tldraw.
import type {
  Geometry2d,
  RecordProps,
  TLBaseShape,
  TLDefaultColorStyle,
  TLDefaultColorTheme,
  TLDefaultFillStyle,
  TLOnResizeHandler,
} from 'tldraw'
import { Polygon2d, resizeBox, ShapeUtil, SVGContainer, T, useDefaultColorTheme, Vec } from 'tldraw'
import 'tldraw/tldraw.css'

type IPadletHalfEllipseShape = TLBaseShape<
  'padlet-half-ellipse',
  {
    w: number
    h: number
    fill: TLDefaultFillStyle
    color: TLDefaultColorStyle
  }
>

// [2]
export default class PadletHalfEllipseShapeUtil extends ShapeUtil<IPadletHalfEllipseShape> {
  static override type = 'padlet-half-ellipse' as const
  static override props: RecordProps<IPadletHalfEllipseShape> = {
    w: T.number,
    h: T.number,
    fill: T.literalEnum('none', 'pattern', 'semi', 'solid'),
    color: T.literalEnum(
      'black',
      'blue',
      'green',
      'grey',
      'light-blue',
      'light-green',
      'light-red',
      'light-violet',
      'orange',
      'red',
      'violet',
      'white',
      'yellow',
    ),
  }

  getDefaultProps(): IPadletHalfEllipseShape['props'] {
    return {
      w: 200,
      h: 200,
      fill: 'none',
      color: 'black',
    }
  }

  override canEdit = (): boolean => false
  override canResize = (): boolean => true
  override isAspectRatioLocked = (): boolean => false

  getGeometry(shape: IPadletHalfEllipseShape): Geometry2d {
    const isFilled = shape.props.fill !== 'none'
    return new Polygon2d({
      points: getHalfEllipsePoints(shape.props.w, shape.props.h),
      isFilled,
    })
  }

  override onResize: TLOnResizeHandler<any> = (shape, info) => {
    return resizeBox(shape, info)
  }

  component(shape: IPadletHalfEllipseShape): JSX.Element | null {
    const theme = useDefaultColorTheme()
    const { w, h, color, fill } = shape.props
    const strokeWidth = 3.5 // https://github.com/tldraw/tldraw/blob/6c846716c343e1ad40839f0f2bab758f58b4284d/packages/tldraw/src/lib/shapes/shared/default-shape-constants.ts#L15
    const radiusX = w / 2
    const radiusY = h / 2
    const centerX = w / 2
    const centerY = h / 2
    const startX = centerX - radiusX
    const endX = centerX + radiusX
    const pathData = `M ${startX} ${centerY} A ${radiusX} ${radiusY} 0 0 1 ${endX} ${centerY}Z`

    // From https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/shapes/shared/ShapeFill.tsx
    interface ShapeFillProps {
      d: string
      fill: TLDefaultFillStyle
      color: TLDefaultColorStyle
      theme: TLDefaultColorTheme
    }
    const ShapeFill = ({ theme, d, color, fill }: ShapeFillProps): JSX.Element | null => {
      switch (fill) {
        case 'none': {
          return null
        }
        case 'solid': {
          return <path fill={theme[color].semi} d={d} />
        }
        case 'semi': {
          return <path fill={theme.solid} d={d} />
        }
        case 'pattern': {
          // For now, we don't support patterns, fallback to semi
          return <path fill={theme.solid} d={d} />
        }
        default: {
          return null
        }
      }
    }

    return (
      <SVGContainer id={shape.id}>
        <>
          <ShapeFill theme={theme} d={pathData} color={color} fill={fill} />
          <g strokeWidth={strokeWidth} stroke={theme[color].solid} fill="none" pointerEvents="all">
            <path d={pathData} fill="transparent" />
          </g>
        </>
      </SVGContainer>
    )
  }

  indicator(shape: IPadletHalfEllipseShape): JSX.Element | null {
    const { w, h } = shape.props
    const radiusX = w / 2
    const radiusY = h / 2
    const centerX = w / 2
    const centerY = h / 2
    const startX = centerX - radiusX
    const endX = centerX + radiusX
    const pathData = `M ${startX} ${centerY} A ${radiusX} ${radiusY} 0 0 1 ${endX} ${centerY}Z`
    return <path d={pathData} />
  }
}

function getHalfEllipsePoints(w: number, h: number): Vec[] {
  const points: Vec[] = []
  const radiusX = w / 2
  const radiusY = h / 2
  const centerX = w / 2
  const centerY = h / 2
  const numPoints = 10
  const angleStep = -Math.PI / numPoints
  for (let i = 0; i <= numPoints; i++) {
    const angle = i * angleStep
    const x = centerX + radiusX * Math.cos(angle)
    const y = centerY + radiusY * Math.sin(angle)
    points.push(new Vec(x, y))
  }
  return points
}
